import React from 'react';
import { AlertTriangle, RefreshCw, TicketIcon, Home } from 'lucide-react';

/**
 * ErrorBoundary - Catches React component errors and offers ticket creation
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log the error
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReportIssue = () => {
    const { error, errorInfo } = this.state;
    const { openTicketModal } = this.props;
    
    if (openTicketModal) {
      openTicketModal({
        errorMessage: error?.message || 'Unknown React Error',
        errorStack: errorInfo?.componentStack || error?.stack || '',
        pageUrl: window.location.href,
        component: 'React Component (Crash)',
      });
    }
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white/5 rounded-2xl border border-red-500/30 overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-red-500/20 to-orange-500/20 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <AlertTriangle className="h-8 w-8 text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Something went wrong</h2>
                  <p className="text-sm text-gray-400">The application encountered an unexpected error</p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            <div className="p-6">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
                <p className="text-sm font-mono text-red-300 break-words">
                  {this.state.error?.message || 'An unexpected error occurred'}
                </p>
              </div>

              {/* Component Stack (collapsible) */}
              {this.state.errorInfo?.componentStack && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400">
                    View technical details
                  </summary>
                  <pre className="mt-2 p-3 bg-black/30 rounded-lg text-xs text-gray-400 overflow-x-auto max-h-32">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <p className="text-sm text-gray-400 mb-6">
                You can try refreshing the page or report this issue to our support team.
              </p>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={this.handleReset}
                  className="w-full py-3 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-white"
                >
                  <RefreshCw size={18} />
                  Try Again
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-gray-300"
                  >
                    <Home size={18} />
                    Go Home
                  </button>
                  
                  {this.props.openTicketModal && (
                    <button
                      onClick={this.handleReportIssue}
                      className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-white"
                    >
                      <TicketIcon size={18} />
                      Report Issue
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
